/**
 * A city and its own centre, for the venue HITL's "use this city's center" autofill.
 *
 * Resolving one takes TWO calls, and that is the whole point of this module.
 * `GET /geodata/explore` — every geodata *search*, including `searchNearest` — answers
 * with the backend's `GeodataSummary` projection, which has **no `geolocation` field at
 * all**. A search hit can therefore never carry coordinates. Reading them off one is
 * what shipped the autofill broken on 2026-09-11: the scrape panel filled the city
 * picker and left the Maps link empty, and the refresh panel, which gated its button on
 * having coordinates, showed no button at all.
 *
 * `GET /geodata/{id}` answers with `GeodataDbDto`, which does carry `geolocation`. So:
 * search (or nearest-search) to find WHICH city, then read that one record for WHERE
 * it is.
 */

export interface CityCenter {
  /** Display name, and the word in the button's label. */
  name: string;
  /** The geodata record submitted alongside the link as `geodata_id`. */
  geodataId: string;
  countryCode?: string;
  region?: string;
  type?: string;
  lat: number;
  lng: number;
}

/** The fields this module needs off a geodata search hit — no coordinates among them. */
interface GeodataHit {
  id: string;
  name: string;
  countryCode?: string;
  region?: string;
  type?: string;
}

/** The single-record read's shape, which is the only place coordinates come from. */
interface GeodataRecord {
  geolocation?: { latitude?: number; longitude?: number } | null;
}

type GetGeodata = (id: string) => Promise<unknown>;
type SearchGeodata = (query: string) => Promise<unknown[]>;

/** The centre of one known geodata record, or undefined when it stores no coordinates. */
export async function readCityCenterById(
  getGeodata: GetGeodata,
  hit: GeodataHit,
): Promise<CityCenter | undefined> {
  const record = (await getGeodata(hit.id)) as GeodataRecord | null;
  const lat = record?.geolocation?.latitude;
  const lng = record?.geolocation?.longitude;
  if (typeof lat !== "number" || typeof lng !== "number") return undefined;
  return {
    name: hit.name,
    geodataId: hit.id,
    countryCode: hit.countryCode,
    region: hit.region,
    type: hit.type,
    lat,
    lng,
  };
}

/** The best-matching city for a name, with its centre. Undefined if there is no match. */
export async function readCityCenter(
  searchGeodata: SearchGeodata,
  getGeodata: GetGeodata,
  cityName: string,
): Promise<CityCenter | undefined> {
  const [match] = (await searchGeodata(cityName)) as GeodataHit[];
  if (!match) return undefined;
  return readCityCenterById(getGeodata, match);
}
